import React from 'react'
import { useNavigate } from 'react-router-dom'
import useFeedbackReply from './hooks/useFeedbackReply'
import AppFormFooterBar from '../../../components/AppFormFooterBar'

export default function FeedbackReplyScreen() {
  const navigate = useNavigate()
  const { state, actions } = useFeedbackReply()
  const { feedbackItem, replyText, loading, isSaving } = state

  if (loading || !feedbackItem) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] flex-col items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-3xl text-primary mb-2">
          progress_activity
        </span>
        <p className="text-xs text-on-surface-variant font-medium">Loading feedback record...</p>
      </div>
    )
  }

  const getTypeBadge = (type = '') => {
    const lower = type.toLowerCase()
    if (lower === 'problem') {
      return 'bg-rose-500/10 text-rose-800 border-rose-500/30'
    }
    if (lower === 'suggestion') {
      return 'bg-emerald-500/10 text-emerald-800 border-emerald-500/30'
    }
    return 'bg-blue-500/10 text-blue-800 border-blue-500/30'
  }

  const titleHeader = `Reply to ${feedbackItem.type} dated ${feedbackItem.date} of ${feedbackItem.name}`

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Header ── */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Cloud &amp; Inquiries
          </p>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-on-surface">
            {titleHeader}
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Compose or edit the administrative resolution communicated to the student.
          </p>
        </div>

      </section>

      {/* ── Original Feedback Overview Card ── */}
      <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs overflow-hidden">
        <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-lg">forum</span>
            </div>
            <div>
              <h2 className="font-display text-sm font-bold text-on-surface">{feedbackItem.name}</h2>
              <p className="text-xs text-on-surface-variant">
                {feedbackItem.courseName} • {feedbackItem.branchName} ({feedbackItem.batchName})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span
              className={`inline-flex items-center rounded-xl border px-3 py-1 text-xs font-extrabold uppercase tracking-wider ${getTypeBadge(
                feedbackItem.type
              )}`}
            >
              {feedbackItem.type}
            </span>
            <span className="rounded-xl bg-surface-container-high px-3 py-1 text-xs font-bold text-on-surface-variant">
              {feedbackItem.date}
            </span>
          </div>
        </div>

        <div className="p-6 md:p-8 flex flex-col gap-2">
          <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
            Student Submission / Query:
          </span>
          <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.04] p-4 text-xs font-medium text-on-surface leading-relaxed">
            {feedbackItem.feedback}
          </div>
        </div>
      </section>

      {/* ── Reply Response Editor Card ── */}
      <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs overflow-hidden">
        <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
            <span className="material-symbols-outlined text-lg">rate_review</span>
          </div>
          <div>
            <h2 className="font-display text-sm font-bold text-on-surface">Institute Reply</h2>
            <p className="text-xs text-on-surface-variant">
              This response will be visible to the student upon logging in on the mobile app.
            </p>
          </div>
        </div>

        <div className="p-6 md:p-8 flex flex-col gap-2">
          <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant">
            Official Response Message
          </label>
          <textarea
            rows={8}
            placeholder="Type your response here..."
            value={replyText}
            onChange={(e) => actions.setReplyText(e.target.value)}
            className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low p-4 text-xs font-medium text-on-surface outline-none focus:border-primary transition leading-relaxed resize-y"
          />
        </div>
      </section>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        onSave={actions.handleSave}
        onCancel={actions.handleCancel}
        isSaving={isSaving}
        saveText="Save & Exit"
        cancelText="Cancel"
      />
    </div>
  )
}
